// Sérialisation / parsing CSV — sous-ensemble RFC 4180 (guillemets doublés,
// séparateur virgule, fins de ligne LF/CRLF).

// Échappe un champ contenant guillemets, virgule ou saut de ligne.
function escapeField(value: unknown): string {
  const s = value == null ? '' : String(value);
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

// Sérialise des lignes en CSV avec en-tête (`columns` = ordre + clés lues).
export function serializeCsv(
  rows: readonly Record<string, unknown>[],
  columns: readonly string[],
): string {
  const header = columns.map(escapeField).join(',');
  const body = rows.map((row) => columns.map((c) => escapeField(row[c])).join(','));
  return [header, ...body].join('\n');
}

export interface ParsedCsv {
  headers: string[];
  rows: Record<string, string>[];
}

// Détecte le séparateur sur l'en-tête : virgule (défaut) ou point-virgule
// (export Excel FR/EU). Compte les occurrences hors guillemets.
function detectSeparator(text: string): ',' | ';' {
  const firstLine = text.split(/\r?\n/, 1)[0] ?? '';
  let commas = 0;
  let semicolons = 0;
  let inQuotes = false;
  for (const ch of firstLine) {
    if (ch === '"') inQuotes = !inQuotes;
    else if (!inQuotes && ch === ',') commas++;
    else if (!inQuotes && ch === ';') semicolons++;
  }
  return semicolons > commas ? ';' : ',';
}

// Parse un CSV en objets `{ header: valeur }`. Séparateur auto-détecté ; lignes vides ignorées.
export function parseCsv(text: string): ParsedCsv {
  const records = tokenize(text, detectSeparator(text));
  if (records.length === 0) return { headers: [], rows: [] };

  // Trim des en-têtes pour tolérer les espaces parasites d'export.
  const headers = records[0]!.map((h) => h.trim());
  const rows: Record<string, string>[] = [];
  for (let i = 1; i < records.length; i++) {
    const fields = records[i]!;
    if (fields.length === 1 && fields[0] === '') continue;
    const row: Record<string, string> = {};
    headers.forEach((h, idx) => {
      row[h] = fields[idx] ?? '';
    });
    rows.push(row);
  }
  return { headers, rows };
}

// Découpe le texte en enregistrements (tableaux de champs) via un automate.
function tokenize(text: string, separator: ',' | ';' = ','): string[][] {
  const records: string[][] = [];
  let field = '';
  let record: string[] = [];
  let inQuotes = false;
  let i = 0;

  const pushField = () => {
    record.push(field);
    field = '';
  };
  const pushRecord = () => {
    pushField();
    records.push(record);
    record = [];
  };

  while (i < text.length) {
    const ch = text[i]!;
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i++;
        continue;
      }
      field += ch;
      i++;
      continue;
    }
    if (ch === '"') {
      inQuotes = true;
      i++;
    } else if (ch === separator) {
      pushField();
      i++;
    } else if (ch === '\n') {
      pushRecord();
      i++;
    } else if (ch === '\r') {
      if (text[i + 1] === '\n') i++; // CRLF
      pushRecord();
      i++;
    } else {
      field += ch;
      i++;
    }
  }
  // Dernier enregistrement sans saut de ligne final.
  if (field !== '' || record.length > 0) pushRecord();
  return records;
}
